import { VoContent, VoMode, Voiceover } from '../types'

import { buildVoiceoverFileName } from './buildFilename'

// Which `texts` keys carry spoken content. Empirically (see the example tours)
// the voiced fields are `text` and `text_1`…`text_N`; quiz answers, buttons,
// titles, alt/accessibility texts are NOT voiced.
const VOICED_KEY = /^text(_\d+)?$/

// Order voiced keys: bare `text` first, then text_1, text_2, … numerically.
function voicedKeyOrder (key: string): number {
  if (key === 'text') return 0
  const m = key.match(/^text_(\d+)$/)
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER
}

function isAudio (mime?: string | null): boolean {
  return !!mime && mime.toLowerCase().startsWith('audio')
}

function voicedKeys (content: VoContent): string[] {
  const texts = content.texts || {}
  return Object.keys(texts)
    .filter((k) => VOICED_KEY.test(k) && (texts[k] || '').trim().length > 0)
    .sort((a, b) => voicedKeyOrder(a) - voicedKeyOrder(b))
}

// Flattens a TourMode's content tree into one row per spoken text, each with a
// suggested file name. Pure + deterministic so it can be sanity-checked against
// a fixture without a running app.
export function extractVoiceovers (
  mode: VoMode | null | undefined,
  ext = 'aac'
): Voiceover[] {
  const rows: Voiceover[] = []
  if (!mode) return rows

  const pois = mode.pois || []
  pois.forEach((poi, pi) => {
    if (!poi) return
    const poiIndex = pi + 1
    const poiTitle = poi.title || ''

    const sections = poi.sections || []
    sections.forEach((section, si) => {
      if (!section) return
      const sectionIndex = si + 1
      const sectionTitle = section.title || ''

      // Content index runs across the whole section (nuggets flattened).
      let contentIndex = 0
      const nuggets = section.nuggets || []
      nuggets.forEach((nugget) => {
        if (!nugget) return
        const nuggetTitle = nugget.title || ''

        const contents = nugget.content || []
        contents.forEach((content) => {
          if (!content) return
          // Markdown blocks are on-screen text, not spoken -> never voiced.
          if (content.type === 'markdown') return
          const keys = voicedKeys(content)
          if (keys.length === 0) return

          contentIndex += 1
          const partCount = keys.length
          const audioFiles = (content.files || []).filter(
            (f): f is NonNullable<typeof f> => !!f && isAudio(f.mimeType)
          )

          keys.forEach((textKey, ki) => {
            const partIndex = ki + 1
            const audio = audioFiles[ki]
            rows.push({
              poiIndex,
              sectionIndex,
              contentIndex,
              partIndex,
              partCount,
              poiTitle,
              sectionTitle,
              nuggetTitle,
              textKey,
              text: (content.texts?.[textKey] || '').trim(),
              suggestedFileName: buildVoiceoverFileName({
                poiTitle,
                poiIndex,
                sectionIndex,
                contentIndex,
                partIndex,
                partCount,
                ext
              }),
              hasAudio: !!audio,
              existingAudioUrl: audio?.url || undefined
            })
          })
        })
      })
    })
  })

  return rows
}
